import React from 'react';
import {
    CAlert,
    CBadge,
    CButton,
    CModal,
    CModalBody,
    CModalHeader,
    CSpinner
} from '@coreui/react';
import StepRuns from './stepRuns';
import {
    InfraNo,
    STEP_CONCLUIDO,
    STEP_ERRO,
    STEP_EXECUTANDO,
    descricaoSituacaoStep,
    rotuloDestino
} from '../../models/InfraPipeline';
import { duracao } from '../../models/InfraRun';

interface Props {
    no: InfraNo | null;
    pipelineId: string;
    versaoRuns: number;
    /** Contador que sobe a cada Executar, para o histórico focar a tentativa nova. */
    execucaoIniciada: number;
    executando: string | null;
    onFechar: () => void;
    /**
     * O segundo argumento pede o reset antes, para passo ja concluido; o
     * terceiro pede a retomada, que pula o que a tentativa anterior fechou.
     */
    onExecutar: (chave: string, resetarAntes?: boolean, retomar?: boolean) => void;
    onResetar: (chave: string) => void;
    onReverter: (no: InfraNo) => void;
    /**
     * Vizinhos deste passo na ordem de leitura, ou null nas pontas. Passar o no
     * inteiro, e nao so a chave, deixa a seta mostrar para onde ela leva.
     */
    anterior: InfraNo | null;
    proximo: InfraNo | null;
    onIr: (chave: string) => void;
}

/**
 * Detalhe de um passo, em modal.
 *
 * Era um card abaixo do fluxo, e o log de execução espremia o DAG até sobrar
 * uma faixa. Em modal o fluxo fica inteiro atrás e o log ganha a altura que
 * precisa, que é o que importa num passo de vinte minutos.
 */
const StepModal = (props: Props) => {
    const { no, pipelineId, versaoRuns, execucaoIniciada, executando, onFechar,
        onExecutar, onResetar, onReverter, anterior, proximo, onIr } = props;

    // Seta do teclado anda entre os passos, igual visualizador de imagem.
    // Ignora quando o foco esta num campo, senao mover o cursor dentro de um
    // input trocaria o passo aberto.
    React.useEffect(() => {
        const aoTeclar = (evento: KeyboardEvent) => {
            const alvo = evento.target as HTMLElement | null;
            const digitando = !!alvo && /^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName);
            if (digitando || evento.ctrlKey || evento.altKey || evento.metaKey) {
                return;
            }

            if (evento.key === 'ArrowLeft' && anterior) {
                onIr(anterior.chave);
            }
            if (evento.key === 'ArrowRight' && proximo) {
                onIr(proximo.chave);
            }
        };

        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [anterior, proximo, onIr]);

    if (!no) {
        return null;
    }

    return (
        <CModal show={true} onClose={onFechar} size="xl" className="infra-modal-step">
            {/* Setas nas laterais, como visualizador de imagem: dá para
                percorrer o fluxo sem fechar e reabrir a cada passo. */}
            {anterior && (
                <button
                    type="button"
                    className="infra-modal-seta infra-modal-seta-antes"
                    onClick={() => onIr(anterior.chave)}
                    title={'Anterior: ' + anterior.numero + '. ' + anterior.rotulo}
                >
                    ‹
                </button>
            )}
            {proximo && (
                <button
                    type="button"
                    className="infra-modal-seta infra-modal-seta-depois"
                    onClick={() => onIr(proximo.chave)}
                    title={'Próximo: ' + proximo.numero + '. ' + proximo.rotulo}
                >
                    ›
                </button>
            )}

            <CModalHeader closeButton>
                <div className="infra-detalhe-titulo">
                    <div>
                        <span className="infra-no-numero mr-2">{no.numero}</span>
                        <strong>{no.rotulo}</strong>
                        <CBadge color="light" className="ml-2">{rotuloDestino(no.destino)}</CBadge>
                        <CBadge
                            color={no.situacao === STEP_ERRO ? 'danger' : 'secondary'}
                            className="ml-2"
                        >
                            {descricaoSituacaoStep(no.situacao)}
                        </CBadge>
                    </div>
                </div>
            </CModalHeader>

            <CModalBody className="infra-modal-step-corpo">
                <p className="infra-detalhe-resumo">{no.resumo}</p>

                <div className="infra-detalhe-deps">
                    {no.depende.length > 0 && (
                        <span>Depende de: {no.depende.join(', ')}. </span>
                    )}
                    {no.tentativas > 0 && (
                        <span>
                            {no.tentativas} tentativa{no.tentativas > 1 ? 's' : ''}
                            {no.run && no.run.ms !== null && ' · última levou ' + duracao(no.run.ms)}
                        </span>
                    )}
                    {/* Passo travado por dependência: o botão some, então o
                        motivo precisa estar escrito, senão a tela não explica
                        por que não dá para rodar. */}
                    {!no.liberado && no.bloqueio && no.situacao !== STEP_CONCLUIDO && (
                        <span className="infra-detalhe-bloqueio">{no.bloqueio}</span>
                    )}
                </div>

                {/* Executar mora dentro do histórico, ao lado das tentativas:
                    é lá que se olha o que aconteceu e se decide rodar de novo.
                    Um "Resetar" num rodapé só dizia o nome de uma operação
                    interna, sem dizer o que ela faz. */}
                <StepRuns
                    pipelineId={pipelineId}
                    chave={no.chave}
                    versao={versaoRuns}
                    execucaoIniciada={execucaoIniciada}
                    onExecutar={no.liberado || no.situacao === STEP_CONCLUIDO || no.situacao === STEP_ERRO
                        ? (retomar?: boolean) => onExecutar(no.chave, no.situacao === STEP_CONCLUIDO, retomar)
                        : undefined}
                    executando={executando === no.chave}
                    ocupado={executando !== null}
                    comErro={no.situacao === STEP_ERRO}
                />

                {/* Reverter fica escondido: apaga banco, bucket e cadastro, e
                    exige senha mestra. Não é ação de rotina. */}
                {no.reversivel && no.situacao === STEP_CONCLUIDO && (
                    <details className="infra-detalhe-perigo">
                        <summary>Desfazer este passo</summary>
                        <p>{no.avisoReversao}</p>
                        <CButton
                            color="danger"
                            variant="outline"
                            size="sm"
                            disabled={executando !== null}
                            onClick={() => onReverter(no)}
                        >
                            Reverter
                        </CButton>
                    </details>
                )}
            </CModalBody>
        </CModal>
    );
};

export default StepModal;
